import * as React from "react";
import { motion } from "framer-motion";

import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  IconButton
} from "@mui/material";
import GithubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import CodeIcon from '@mui/icons-material/Code';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Profile from "../assets/profile/profile.png";

export default function Home() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
    },
  };

  return (
    <Box
      id="home"
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        pt: { xs: 16, md: 24 },
        pb: 12,
        position: "relative",
      }}
    >
      <Container maxWidth="lg">
        <Box
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Hero Header */}
          <Box sx={{ mb: 8, textAlign: { xs: "left", md: "center" } }}>
            <motion.div variants={itemVariants}>
              <Typography
                variant="h1"
                sx={(theme) => ({
                  color: "text.primary",
                  mb: 3,
                  background: theme.palette.mode === 'dark' ? "linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0.5) 100%)" : "linear-gradient(180deg, #111111 0%, rgba(17, 17, 17, 0.5) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                })}
              >
                Harits Raihan
              </Typography>
            </motion.div>
            <motion.div variants={itemVariants}>
              <Typography
                variant="h4"
                sx={{
                  color: "text.secondary",
                  maxWidth: 600,
                  mx: { md: "auto" },
                  fontWeight: 400
                }}
              >
                Software Engineer building high-performance web and mobile applications.
              </Typography>
            </motion.div>
          </Box>

          {/* Mini Bento Grid Intro */}
          <Grid container spacing={3}>
            {/* Main Profile Bento */}
            <Grid item xs={12} md={8}>
              <motion.div variants={itemVariants} style={{ height: "100%" }}>
                <Card sx={{ height: "100%", display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", p: 2 }}>
                  <Box sx={{ p: 2, flex: 1 }}>
                    <Typography variant="body1" sx={{ fontSize: "1.125rem", mb: 3 }}>
                      I specialize in translating complex requirements into elegant, modular, and maintainable systems. Currently focused on modern React, Flutter, and scalable backend architectures.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {[
                        { icon: <GithubIcon />, href: "https://github.com/haritsrhn" },
                        { icon: <LinkedInIcon />, href: "https://www.linkedin.com/in/haritsrhn/" },
                        { icon: <InstagramIcon />, href: "https://instagram.com/haritsrhn" }
                      ].map((social, i) => (
                        <IconButton
                          key={i}
                          href={social.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={social.href}
                          sx={(theme) => ({
                            bgcolor: "background.default",
                            "&:hover": { bgcolor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)" }
                          })}
                        >
                          {social.icon}
                        </IconButton>
                      ))}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: { xs: "100%", sm: 200 },
                      height: 200,
                      borderRadius: "16px",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={Profile}
                      alt="Harits Raihan"
                      style={{ width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(100%)" }}
                    />
                  </Box>
                </Card>
              </motion.div>
            </Grid>

            {/* Quick Stats / Info Bentos */}
            <Grid item xs={12} sm={6} md={4}>
              <motion.div variants={itemVariants} style={{ height: "100%" }}>
                <Card sx={{ height: "100%", p: 4, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <LocationOnIcon sx={{ color: "primary.main", fontSize: 32, mb: 2 }} />
                  <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>Location</Typography>
                  <Typography variant="h4" sx={{ color: "text.primary" }}>Medan, Indonesia</Typography>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <motion.div variants={itemVariants} style={{ height: "100%" }}>
                <Card sx={{ height: "100%", p: 4, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <CodeIcon sx={{ color: "primary.main", fontSize: 32, mb: 2 }} />
                  <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>Focus</Typography>
                  <Typography variant="h4" sx={{ color: "text.primary" }}>Full-Stack</Typography>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={8}>
              <motion.div variants={itemVariants} style={{ height: "100%" }}>
                <Card
                  component="a"
                  href="https://drive.google.com/file/d/1lQYTRTKqMl_G6x4ILNuK12KxqPuRJ5ty/view?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={(theme) => ({
                    height: "100%",
                    p: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    textDecoration: "none",
                    background: theme.palette.mode === 'dark' ? "linear-gradient(45deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.08) 100%)" : "linear-gradient(45deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.06) 100%)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                    }
                  })}
                >
                  <Box>
                    <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>Status</Typography>
                    <Typography variant="h4" sx={{ color: "text.primary" }}>Available for work</Typography>
                  </Box>
                  <Box
                    sx={(theme) => ({
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: theme.palette.primary.contrastText
                    })}
                  >
                    →
                  </Box>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
